import { ICoupon } from '../utils/entities/interface';
import { Pagination } from './booking';
import { get, post } from './https';

interface CoupenListResponse {
  status: boolean;
  data: ICoupon[];
  count: number;
  pagination: Pagination;
}
const GETCoupons = async ({
  page = 1,
  limit = 25,
  searchQuery = '',
  sortField = 'name',
  sortOrder = 'ASC',
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
}): Promise<CoupenListResponse> => {
  // Initialize the base URL
  let url = `/api/v1/admin/coupon/list?limit=${limit}&page=${page}`;

  // Conditionally add search parameter if provided
  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  // Conditionally add sortField and sortOrder if they are provided
  if (sortField && sortField !== 'name') {
    // assuming 'name' is the default field, you can change it if needed
    url += `&sortField=${sortField}`;
  }

  if (sortOrder && sortOrder !== 'ASC') {
    // assuming 'ASC' is the default sort order
    url += `&sortOrder=${sortOrder}`;
  }

  // Fetch the response
  const response = await post(url); // Use the previously defined `get` function

  if (response.status == 200) {
    return response.data; // If status is true, return the user data
  } else {
    throw new Error('Failed to fetch users');
  }
};

interface AddEditCouponResponse {
  status: boolean;
  data: ICoupon;
  message: string;
}

const addEditCoupon = async (
  coupon: Omit<ICoupon, '_id' | 'createdAt' | 'updatedAt'> & { _id?: string },
): Promise<AddEditCouponResponse> => {
  const url = '/api/v1/admin/coupon/add-edit';

  // Create the request body. expiryDate is sent as local-date components
  // (not toISOString, which shifts the date to UTC and can push the day
  // boundary into the previous/next day depending on the browser's
  // timezone) so the backend stores the same calendar date the admin
  // picked.
  const date = coupon.expiryDate;
  const localDateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const requestBody = {
    couponCode: coupon.couponCode,
    couponObjectid: coupon._id || '',
    image: coupon.image,
    name: coupon.name,
    discount: coupon.discount,
    minValue: coupon.minValue,
    expiryDate: localDateString,
    description: coupon.description,
    // isActive: coupon.isActive,
  };

  // Send the POST request
  const response = await post(url, requestBody);

  // Create returns 201, edit returns 200 — both are success.
  if (response.status === 201 || response.status === 200) {
    return response.data;
  } else {
    throw new Error('Failed to add or edit coupon');
  }
};

const ToggleCouponStatus = async (couponId: string): Promise<number> => {
  const response = await get(
    `/api/v1/admin/coupon/active-inactive/${couponId}`,
  );

  if (response.status == 200) {
    return 200;
  } else {
    throw new Error(response.statusText || 'Failed to change user status');
  }
};

export { GETCoupons, addEditCoupon, ToggleCouponStatus };
